/**
 * „Ort teilen, solange die App offen ist“ (27.10.2026).
 *
 * Eine Zeile je Sitzung (`kind: live`), alle `LIVE_UPDATE_MS` neu geortet —
 * **nur im Vordergrund**. Geht die App in den Hintergrund oder wird sie
 * geschlossen, endet das Teilen: iOS ließe eine Web-App dort ohnehin nicht
 * orten, und ein „teilt gerade“, das nicht mehr stimmt, wäre schlimmer als
 * keins. Die Ansicht sagt das ausdrücklich.
 *
 * Bewusst außerhalb einer Komponente: Ein Wechsel des Reiters beendet das
 * Teilen nicht — die App ist ja noch offen.
 */
import { LIVE_UPDATE_MS, placeAt } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';
import { checkIn, listPlaces, stopLive, updateLive } from '../db/location.js';
import { geolocate } from './geolocate.js';
import type { GeoFailure } from './geolocate.js';
import { GeoError } from './geolocate.js';

export interface LiveState {
  id: string;
  until: string;
  /** Letzte Ortung fehlgeschlagen — das Teilen läuft, der Punkt ist älter. */
  lastError: GeoFailure | null;
}

let state: LiveState | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();
let database: KraftwerkDatabase = db;
let locate: typeof geolocate = geolocate;

function emit(): void {
  for (const listener of listeners) listener();
}

export function liveState(): LiveState | null {
  return state;
}

export function subscribeLive(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function nearest(householdId: string, fix: { latitude: number; longitude: number }) {
  const place = placeAt(fix, await listPlaces(householdId, database));
  return place ? { id: place.id, name: place.name } : null;
}

async function refresh(householdId: string): Promise<void> {
  const current = state;
  if (!current) return;
  try {
    const fix = await locate();
    if (state?.id !== current.id) return;
    await updateLive(current.id, fix, await nearest(householdId, fix), database);
    state = { ...current, lastError: null };
  } catch (error) {
    if (state?.id !== current.id) return;
    state = { ...current, lastError: error instanceof GeoError ? error.reason : 'unavailable' };
  }
  emit();
}

function onHidden(): void {
  if (document.visibilityState === 'hidden') void stopLiveShare();
}

/** Startet das Teilen mit einem schon bestimmten ersten Punkt. */
export async function startLiveShare(input: {
  householdId: string;
  minutes: number;
  fix: Awaited<ReturnType<typeof geolocate>>;
  place: { id: string; name: string } | null;
}): Promise<LiveState> {
  await stopLiveShare();
  const until = new Date(Date.now() + input.minutes * 60 * 1000).toISOString();
  const row = await checkIn(
    {
      householdId: input.householdId,
      kind: 'live',
      fix: input.fix,
      place: input.place,
      liveUntil: until,
    },
    database,
  );
  state = { id: row.id, until, lastError: null };
  timer = setInterval(() => void refresh(input.householdId), LIVE_UPDATE_MS);
  stopTimer = setTimeout(() => void stopLiveShare(), input.minutes * 60 * 1000);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  emit();
  return state;
}

function onPageHide(): void {
  void stopLiveShare();
}

export async function stopLiveShare(): Promise<void> {
  const current = state;
  if (timer) clearInterval(timer);
  if (stopTimer) clearTimeout(stopTimer);
  timer = null;
  stopTimer = null;
  state = null;
  document.removeEventListener('visibilitychange', onHidden);
  window.removeEventListener('pagehide', onPageHide);
  if (current) {
    emit();
    await stopLive(current.id, database);
  }
}

/** Für Tests: andere Datenbank, andere Ortung. */
export function configureLiveShare(options: {
  database?: KraftwerkDatabase;
  locate?: typeof geolocate;
}): void {
  database = options.database ?? db;
  locate = options.locate ?? geolocate;
}
